import { useEffect, useId, useRef, useState } from "react";
import { corFilamentoHex } from "../../../utils/corFilamento";
import { CalcIcon } from "../CalcIcon";
import { SeletorFilamentoInline } from "./SeletorFilamentoInline";
import "./fields.css";

const moeda = (valor) => (Number(valor) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Busca e adição de múltiplos filamentos, incluindo as cores importadas de um 3MF.
export function FilamentoPicker({ filamentos, filamentosCalc, adicionarFilamento, removerFilamento, coresCustom, upload3mf, titulo, mostrarBotaoImportar = true, ocultar = false }) {
  const fieldId = useId();
  const listaRef = useRef(null);
  const [busca, setBusca] = useState("");
  const [filId, setFilId] = useState("");
  const [peso, setPeso] = useState("");
  const [aberto, setAberto] = useState(false);
  const [ativo, setAtivo] = useState(-1);

  const filtrados = filamentos.filter(f => {
    if (!busca.trim()) return true;
    const q = busca.trim().toLowerCase();
    return [f.cor, f.nome, f.marca, f.material].some(valor => (valor || "").toLowerCase().includes(q));
  });

  useEffect(() => {
    if (aberto && ativo >= 0) {
      listaRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: "nearest" });
    }
  }, [ativo, aberto]);

  const selecionar = (f) => {
    setFilId(f.id);
    setBusca(f.cor || f.nome || "");
    setAberto(false);
    setAtivo(-1);
  };

  const confirmar = () => {
    if (!filId) return;
    adicionarFilamento(filId, peso);
    if (!(parseFloat(peso) > 0)) return;
    setFilId("");
    setPeso("");
    setBusca("");
    setAberto(false);
    setAtivo(-1);
  };

  const navegarBusca = (event) => {
    if (event.key === "Escape") {
      setAberto(false);
      setAtivo(-1);
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setAberto(true);
      if (filtrados.length) {
        const direcao = event.key === "ArrowDown" ? 1 : -1;
        setAtivo(indice => indice < 0 ? (direcao > 0 ? 0 : filtrados.length - 1) : (indice + direcao + filtrados.length) % filtrados.length);
      }
    } else if (event.key === "Enter" && aberto && filtrados.length) {
      event.preventDefault();
      selecionar(filtrados[ativo >= 0 && ativo < filtrados.length ? ativo : 0]);
    }
  };

  const fSel = filamentos.find(f => f.id === filId);
  const estoqueBaixo = (() => {
    if (!fSel || !(parseFloat(fSel.peso) > 0) || fSel.peso_restante == null) return null;
    const pct = Math.round(parseFloat(fSel.peso_restante) / parseFloat(fSel.peso) * 100);
    return pct < 20 ? { pct, restante: Math.round(parseFloat(fSel.peso_restante)) } : null;
  })();
  const coresDetectadas = upload3mf?.coresDetectadas;

  return (
    <div className="calc-filament-picker" data-tour="calc-filamentos-secao" data-count={filamentosCalc.length}>
      {(titulo || (upload3mf && mostrarBotaoImportar)) && (
        <div className="calc-filament-heading">
          {titulo && <span className="calc-filament-title">{titulo}</span>}
          {upload3mf && mostrarBotaoImportar && (
            <button type="button" className="calc-filament-import" onClick={upload3mf.onAbrir} disabled={upload3mf.fatiando}
              title="Importa impressora, cores, peso e tempo disponíveis no arquivo 3MF">
              <CalcIcon name="upload" size={16} />
              {upload3mf.fatiando ? "Lendo arquivo..." : "Importar 3MF"}
            </button>
          )}
        </div>
      )}

      {coresDetectadas && (
        <div className="calc-filament-detected">
          <p className="calc-filament-detected-title">Filamentos detectados</p>
          <p className="calc-field-help">Confirme o filamento e o peso de cada cor.</p>
          <div className="calc-filament-detected-list">
            {coresDetectadas.map((cd, i) => (
              <div key={i} className="calc-filament-detected-row">
                <span className="calc-filament-swatch" style={{ background: cd.colorHex || "#888" }} title={cd.colorHex || "Cor não identificada"} />
                <div className="calc-filament-detected-picker" role="group" aria-label={`Filamento da cor ${i + 1}`}>
                  <SeletorFilamentoInline
                    filamentoId={cd.filamentoId} filamentos={filamentos} coresCustom={coresCustom}
                    onSelect={id => upload3mf.onAlterarCor(i, { filamentoId: id })}
                  />
                </div>
                <div className="calc-field-unit calc-filament-detected-weight">
                  <input id={`${fieldId}-cor-${i}`} className="calc-field-input" type="number" min="0" step="any" inputMode="decimal"
                    value={cd.peso_g ?? ""} placeholder="0" aria-label={`Peso em gramas da cor ${i + 1}`}
                    onChange={e => upload3mf.onAlterarCor(i, { peso_g: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                  <label htmlFor={`${fieldId}-cor-${i}`}>g</label>
                </div>
                <button type="button" className="calc-filament-remove" onClick={() => upload3mf.onRemoverCor(i)} aria-label={`Remover cor ${i + 1}`} title="Remover esta cor">
                  <CalcIcon name="trash" size={17} />
                </button>
              </div>
            ))}
          </div>
          <div className="calc-filament-detected-actions">
            <button type="button" className="calc-filament-add" onClick={upload3mf.onConfirmar}><CalcIcon name="plus" size={18} /> Adicionar todos</button>
            <button type="button" className="calc-filament-cancel" onClick={upload3mf.onCancelar}>Cancelar</button>
          </div>
        </div>
      )}

      {filamentos.length === 0 ? (
        <p className="calc-field-empty">Cadastre um filamento na aba Filamentos primeiro.</p>
      ) : (
        <>
          <div data-tour="calc-busca-linha" className="calc-filament-entry">
            <div data-tour="calc-busca-filamento" className="calc-filament-search">
              <label className="calc-field-label" htmlFor={`${fieldId}-busca`}>Filamento</label>
              <div className="calc-filament-search-input">
                <CalcIcon name="search" size={17} />
                <input id={`${fieldId}-busca`} className="calc-field-input" value={busca} role="combobox"
                  aria-autocomplete="list" aria-expanded={aberto} aria-controls={`${fieldId}-opcoes`}
                  aria-activedescendant={aberto && ativo >= 0 && ativo < filtrados.length ? `${fieldId}-opcao-${ativo}` : undefined}
                  onChange={e => { setBusca(e.target.value); setFilId(""); setAberto(true); setAtivo(-1); }}
                  onFocus={() => setAberto(true)} onBlur={() => { setAberto(false); setAtivo(-1); }}
                  onKeyDown={navegarBusca} placeholder="Buscar cor, material ou marca..." autoComplete="off" />
              </div>
              {aberto && (
                <div data-tour="calc-busca-dropdown" className="calc-filament-options" id={`${fieldId}-opcoes`} role="listbox" aria-label="Filamentos disponíveis" ref={listaRef}>
                  {filtrados.length === 0 && <div className="calc-filament-no-results" role="status">Nenhum filamento encontrado</div>}
                  {filtrados.map((f, i) => (
                    <div key={f.id} id={`${fieldId}-opcao-${i}`} className="calc-filament-option" role="option" aria-selected={ativo === i}
                      onMouseDown={e => e.preventDefault()} onClick={() => selecionar(f)} onMouseMove={() => setAtivo(i)}>
                      <span className="calc-filament-swatch" style={{ background: corFilamentoHex(f.cor, coresCustom) }} />
                      <span className="calc-filament-option-info">
                        <span className="calc-filament-name">{f.cor || f.nome}</span>
                        <span className="calc-filament-meta">{[f.material, f.marca].filter(Boolean).join(" · ")}</span>
                      </span>
                      <span className="calc-filament-option-price">R$ {ocultar ? "••••" : moeda(f.custo)}<small>/kg</small></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="calc-filament-weight">
              <label className="calc-field-label" htmlFor={`${fieldId}-peso`}>Peso</label>
              <div className="calc-field-unit">
                <input id={`${fieldId}-peso`} className="calc-field-input" type="number" min="0" step="any" inputMode="decimal" value={peso}
                  onChange={e => setPeso(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); confirmar(); } }} placeholder="0" />
                <span aria-hidden="true">g</span>
              </div>
            </div>
            <button type="button" data-tour="calc-add-filamento" className="calc-filament-add" onClick={confirmar} disabled={!filId} aria-label="Adicionar filamento">
              <CalcIcon name="plus" size={18} /> Adicionar
            </button>
          </div>

          {estoqueBaixo && (
            <p className="calc-filament-low-stock" role="status">
              Estoque baixo: <b>{fSel.cor || fSel.nome}</b> está em {estoqueBaixo.pct}% ({estoqueBaixo.restante} g restantes).
            </p>
          )}
        </>
      )}

      {filamentosCalc.length > 0 && (
        <ul className="calc-filament-selected" aria-label="Filamentos adicionados">
          {filamentosCalc.map(f => (
            <li key={f.id} className="calc-filament-selected-row">
              <span className="calc-filament-swatch" style={{ background: corFilamentoHex(f.nome, coresCustom) }} />
              <span className="calc-filament-selected-info">
                <span className="calc-filament-name" title={f.nome}>{f.nome}</span>
                <span className="calc-filament-meta">{f.material}{f.material && " · "}{f.peso} g</span>
              </span>
              <span className="calc-filament-cost">R$ {ocultar ? "••••" : moeda(f.custo_total)}</span>
              <button type="button" className="calc-filament-remove" onClick={() => removerFilamento(f.id)} aria-label={`Remover filamento ${f.nome}`} title="Remover filamento">
                <CalcIcon name="trash" size={17} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
